import React, { useState, useEffect, useContext } from 'react';
import { Button, TextInput, Card, AuthContext } from 'components/lib';
import axios from 'axios';
import { connectApp } from '../client/connections/appConnectionLogic';
import apps from '../../constants/apps.json';
import styles from './NewAccountWizard.module.css';

export function NewAccountWizard({ onSave, onCancel, editData, entityTerm = 'Client' }) {
  const authContext = useContext(AuthContext);
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [accountData, setAccountData] = useState({
    name: '',
    email: '',
    monitoringLevel: 'full_monitoring',
    dataSource: null
  });
  
  // Form state
  const [monitoringLimits, setMonitoringLimits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Get proper plural form
  const entityTermPlural = entityTerm === 'Company' ? 'Companies' : 
                          entityTerm === 'Client' ? 'Clients' : 
                          `${entityTerm}s`;

  // Get only data apps that are available
  const dataApps = apps.filter(app => app.type === 'data' && !app.coming_soon);

  useEffect(() => {
    // Fetch monitoring limits
    const fetchLimits = async () => {
      try {
        const response = await axios.get('/api/account/monitoring-limits');
        setMonitoringLimits(response.data);
        
        // Set default to premium monitoring if allowed
        if (response.data.canUpgradeToFullMonitoring) {
          setAccountData(prev => ({ ...prev, monitoringLevel: 'full_monitoring' }));
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching monitoring limits:', error);
        setLoading(false);
      }
    };
    fetchLimits();
  }, []);

  const canSelectFullMonitoring = () => {
    if (!monitoringLimits) return false;
    return monitoringLimits.canUpgradeToFullMonitoring;
  };

  const handleNext = () => {
    if (currentStep === 1 && accountData.name.trim()) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    setSaving(true);
    
    try {
      // Step 1: Create the account
      const accountPayload = {
        name: accountData.name,
        email: accountData.email,
        active: true,
        account_type: accountData.monitoringLevel
      };
      
      const response = await axios.post('/api/account', accountPayload);
      const newAccountId = response.data.account.id;
      
      // Step 2: If a data source was selected, redirect with connect parameter
      if (accountData.dataSource) {
        // Switch to the new account and go to connections page with connect parameter
        const redirectUrl = `/client/connections?connect=${accountData.dataSource}`;
        authContext.switchAccount(newAccountId, redirectUrl);
      } else {
        // No data source selected, just notify parent
        onSave(response.data.account);
      }
      
    } catch (error) {
      console.error('Failed to create account:', error);
      setSaving(false);
    }
  };

  const selectDataSource = (appShortName) => {
    setAccountData(prev => ({ ...prev, dataSource: appShortName }));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className={styles.stepContent}>
            <h2>Let's add your {entityTermLower}</h2>
            <p className={styles.stepDescription}>
              First, we'll need some basic information about this {entityTermLower}.
            </p>
            
            <div className={styles.formGroup}>
              <label>{entityTerm} Name</label>
              <TextInput 
                value={accountData.name} 
                onChange={(name, value) => setAccountData(prev => ({ ...prev, name: value }))}
                placeholder={`Enter ${entityTermLower} name`}
                autoFocus
              />
            </div>

            <div className={styles.formGroup}>
              <label>{entityTerm} Email (Optional)</label>
              <TextInput 
                value={accountData.email} 
                onChange={(name, value) => setAccountData(prev => ({ ...prev, email: value }))}
                placeholder="contact@example.com"
                type="email"
              />
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className={styles.stepContent}>
            <h2>Choose monitoring level</h2>
            <p className={styles.stepDescription}>
              Select how you'd like to monitor this {entityTermLower}'s financial data.
            </p>
            
            <div className={styles.monitoringOptions}>
              {/* Standard Monitoring Option */}
              <div 
                className={`${styles.monitoringOption} ${accountData.monitoringLevel === 'health_score_only' ? styles.selected : ''}`}
                onClick={() => setAccountData(prev => ({ ...prev, monitoringLevel: 'health_score_only' }))}
              >
                <div className={styles.optionHeader}>
                  <input
                    type="radio"
                    name="monitoringLevel"
                    value="health_score_only"
                    checked={accountData.monitoringLevel === 'health_score_only'}
                    onChange={(e) => setAccountData(prev => ({ ...prev, monitoringLevel: e.target.value }))}
                  />
                  <strong>Standard Monitoring</strong>
                  <span className={styles.badge}>BASIC</span>
                </div>
                <div className={styles.optionFeatures}>
                  <div>✓ Health score tracking</div>
                  <div>✓ Basic data alerts</div>
                </div>
              </div>

              {/* Premium Monitoring Option */}
              <div 
                className={`${styles.monitoringOption} ${accountData.monitoringLevel === 'full_monitoring' ? styles.selected : ''} ${!canSelectFullMonitoring() ? styles.disabled : ''}`}
                onClick={() => canSelectFullMonitoring() && setAccountData(prev => ({ ...prev, monitoringLevel: 'full_monitoring' }))}
              >
                <div className={styles.optionHeader}>
                  <input
                    type="radio"
                    name="monitoringLevel"
                    value="full_monitoring"
                    checked={accountData.monitoringLevel === 'full_monitoring'}
                    onChange={(e) => setAccountData(prev => ({ ...prev, monitoringLevel: e.target.value }))}
                    disabled={!canSelectFullMonitoring()}
                  />
                  <strong>Premium Monitoring</strong>
                  <span className={`${styles.badge} ${styles.recommended}`}>RECOMMENDED</span>
                </div>
                <div className={styles.optionFeatures}>
                  <div>✓ Everything in Standard</div>
                  <div>✓ Advanced cleanup tools</div>
                  <div>✓ Transaction categorization</div>
                  <div>✓ Custom audit rules</div>
                </div>
                {!canSelectFullMonitoring() && monitoringLimits && (
                  <div className={styles.limitWarning}>
                    <strong>💡 Tip:</strong> You've used all 3 premium slots. You can upgrade {entityTermPlural.toLowerCase()} later from their settings.
                  </div>
                )}
              </div>
            </div>

            {monitoringLimits && (
              <div className={styles.monitoringInfo}>
                <div className={styles.infoRow}>
                  <span>{monitoringLimits.reason}</span>
                  <span className={styles.usageCount}>{monitoringLimits.currentFullyMonitoredCount} used</span>
                </div>
                <div>All {entityTermPlural.toLowerCase()} get health score tracking. You can change monitoring levels anytime.</div>
              </div>
            )}
          </div>
        );
        
      case 3:
        return (
          <div className={styles.stepContent}>
            <h2>Connect your accounting system</h2>
            <p className={styles.stepDescription}>
              Choose how you'd like to import {accountData.name}'s financial data. You can also do this later.
            </p>
            
            <div className={styles.dataSourceGrid}>
              {dataApps.map((app) => (
                <div 
                  key={app.short_name}
                  className={`${styles.dataSourceOption} ${accountData.dataSource === app.short_name ? styles.selected : ''}`}
                  onClick={() => selectDataSource(app.short_name)}
                >
                  <img src={app.icon} alt={app.name} />
                  <span>{app.name}</span>
                  {accountData.dataSource === app.short_name && (
                    <div className={styles.checkmark}>✓</div>
                  )}
                </div>
              ))}
            </div>
            
            <div className={styles.skipOption}>
              <button 
                className={styles.skipLink}
                onClick={() => setAccountData(prev => ({ ...prev, dataSource: null }))}
              >
                I'll connect later →
              </button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  const entityTermLower = entityTerm.toLowerCase();

  return (
    <div className={styles.wizardContainer}>
      {/* Progress indicator */}
      <div className={styles.progressBar}>
        <div className={`${styles.progressStep} ${currentStep >= 1 ? styles.active : ''}`}>
          <div className={styles.stepNumber}>1</div>
          <div className={styles.stepLabel}>{entityTerm} Info</div>
        </div>
        <div className={`${styles.progressStep} ${currentStep >= 2 ? styles.active : ''}`}>
          <div className={styles.stepNumber}>2</div>
          <div className={styles.stepLabel}>Monitoring</div>
        </div>
        <div className={`${styles.progressStep} ${currentStep >= 3 ? styles.active : ''}`}>
          <div className={styles.stepNumber}>3</div>
          <div className={styles.stepLabel}>Data Source</div>
        </div>
      </div>

      {/* Step content */}
      {renderStep()}

      {/* Navigation buttons */}
      <div className={styles.buttonGroup}>
        {currentStep > 1 && (
          <Button 
            text="Back" 
            action={handleBack} 
            color="secondary"
          />
        )}
        
        <div className={styles.rightButtons}>
          <Button 
            text="Cancel" 
            action={onCancel} 
            color="secondary"
          />
          
          {currentStep < 3 ? (
            <Button 
              text="Next" 
              action={handleNext} 
              disabled={currentStep === 1 && !accountData.name.trim()}
              color="primary"
            />
          ) : (
            <Button 
              text={accountData.dataSource ? "Create & Connect" : "Create " + entityTerm} 
              action={handleFinish} 
              disabled={saving}
              color="primary"
            />
          )}
        </div>
      </div>
    </div>
  );
}